import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Phone, Mail, Search, Trash2 } from "lucide-react";
import { adminApi, formatErr } from "@/lib/api";
import { Logo } from "@/components/site/Logo";

const STATUS = [["nouveau", "Nouveau", "bg-gold text-ink"], ["en_cours", "En cours", "bg-ink text-paper"], ["traite", "Traité", "bg-emerald-700 text-white"]];
const LABEL = {
  interieur: "Intérieur", exterieur: "Extérieur / façade", revetements: "Revêtements", autre: "Autre",
  appartement: "Appartement", maison: "Maison", copropriete: "Copropriété", local: "Local pro",
  urgent: "Urgent", "1-3mois": "1 à 3 mois", "3mois+": "+3 mois", flexible: "Flexible",
};

const QuoteRow = ({ q, onStatus, onDelete }) => (
  <article data-testid={`admin-quote-${q.id}`} className="grid gap-5 border border-paper-line bg-white p-6 lg:grid-cols-12">
    <div className="lg:col-span-4">
      <p className="font-mono text-[11px] text-slate-600">{new Date(q.created_at).toLocaleString("fr-FR")}</p>
      <h3 className="mt-1 font-display text-2xl">{q.name}</h3>
      <p className="text-sm text-slate-700">{q.city}</p>
      <div className="mt-3 space-y-1 text-sm">
        <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-gold-deep"><Phone className="h-3.5 w-3.5" />{q.phone}</a>
        <a href={`mailto:${q.email}`} className="flex items-center gap-2 hover:text-gold-deep"><Mail className="h-3.5 w-3.5" />{q.email}</a>
      </div>
    </div>
    <div className="lg:col-span-5 text-sm">
      <div className="flex flex-wrap gap-2">
        {[q.work_type, q.property_type, q.timeline].map((k) => <span key={k} className="border border-paper-line px-2.5 py-1">{LABEL[k] || k}</span>)}
        {q.surface ? <span className="border border-paper-line px-2.5 py-1">{q.surface} m²</span> : null}
      </div>
      <p className="mt-3 text-slate-700 whitespace-pre-line">{q.message || "—"}</p>
    </div>
    <div className="lg:col-span-3 flex flex-wrap items-start gap-2 lg:justify-end">
      {STATUS.map(([v, l, c]) => (
        <button key={v} onClick={() => onStatus(q.id, v)} data-testid={`admin-status-${q.id}-${v}`}
          className={`px-3 py-1.5 text-xs font-semibold border transition-colors ${q.status === v ? `${c} border-transparent` : "border-paper-line text-slate-600 hover:border-ink"}`}>
          {l}
        </button>
      ))}
      <button onClick={() => onDelete(q.id)} data-testid={`admin-delete-${q.id}`} aria-label="Supprimer" className="p-1.5 text-slate-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
    </div>
  </article>
);

export default function AdminQuotes() {
  const nav = useNavigate();
  const [quotes, setQuotes] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const fail = (e) => {
    if (e.response?.status === 401) { localStorage.removeItem("spd_token"); nav("/admin"); return; }
    toast.error(formatErr(e.response?.data?.detail));
  };

  useEffect(() => {
    adminApi().get("/admin/quotes").then((r) => setQuotes(Array.isArray(r.data) ? r.data : [])).catch(fail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onStatus = async (id, status) => {
    try {
      const { data } = await adminApi().patch(`/admin/quotes/${id}`, { status });
      setQuotes((l) => l.map((x) => (x.id === id ? data : x)));
    } catch (e) { fail(e); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    try { await adminApi().delete(`/admin/quotes/${id}`); setQuotes((l) => l.filter((x) => x.id !== id)); } catch (e) { fail(e); }
  };
  const logout = () => { localStorage.removeItem("spd_token"); nav("/admin"); };

  const list = useMemo(() => (quotes || []).filter((x) =>
    (filter === "all" || x.status === filter) &&
    `${x.name} ${x.city} ${x.email} ${x.phone}`.toLowerCase().includes(q.toLowerCase())), [quotes, q, filter]);
  const count = (s) => (quotes || []).filter((x) => x.status === s).length;

  return (
    <main data-testid="admin-quote-list" className="min-h-screen bg-paper">
      <header className="flex items-center justify-between border-b border-paper-line px-5 py-4 sm:px-8">
        <Link to="/"><Logo /></Link>
        <nav className="flex items-center gap-6 text-sm">
          <Link to="/admin/avis" data-testid="admin-nav-avis" className="link-line">Avis Google</Link>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2"><LogOut className="h-4 w-4" />Déconnexion</button>
        </nav>
      </header>
      <div className="mx-auto max-w-[1400px] px-5 py-12 sm:px-8">
        <h1 className="font-display text-5xl">Demandes de devis</h1>
        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {[["all", `Toutes (${quotes?.length ?? 0})`], ...STATUS.map(([v, l]) => [v, `${l} (${count(v)})`])].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`admin-filter-${v}`}
                className={`px-4 py-2 text-sm border ${filter === v ? "bg-ink text-paper border-ink" : "border-paper-line hover:border-ink"}`}>{l}</button>
            ))}
          </div>
          <label className="flex items-center gap-2 border-b border-ink/20 py-2 lg:w-80">
            <Search className="h-4 w-4 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher nom, ville…" data-testid="admin-search-input" className="w-full bg-transparent outline-none" />
          </label>
        </div>
        <div className="mt-8 space-y-3">
          {quotes === null && <p className="text-slate-600">Chargement…</p>}
          {quotes && list.length === 0 && <p className="text-slate-600" data-testid="admin-empty">Aucune demande.</p>}
          {list.map((x) => <QuoteRow key={x.id} q={x} onStatus={onStatus} onDelete={onDelete} />)}
        </div>
      </div>
    </main>
  );
}
